import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, Star, Clock, ShieldCheck, Wallet, Truck, ArrowRight,
  CheckCircle2, MapPin, MessageSquare, Award,
} from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import EmergencyBanner from "@/components/EmergencyBanner";
import { SITE, PRIMARY_PHONE } from "@/lib/site";
import { SERVICES } from "@/lib/services";

const TESTIMONIALS = [
  { name: "Liam J.", quote: "Really cheap price and reliable, highly recommended.", role: "Local driver, Sale" },
  { name: "Emily Brown", quote: "Great business, cheap price, very helpful — saved my night.", role: "Customer, Melbourne" },
  { name: "David Wilson", quote: "Arrived quickly and very professional, top job.", role: "Customer, Albion" },
];

const HERO_IMG = "https://images.pexels.com/photos/13151224/pexels-photo-13151224.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden bg-white">
    <div className="absolute inset-0 -z-10">
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-blue-100 blur-3xl opacity-60" />
      <div className="absolute top-40 right-0 h-80 w-80 rounded-full bg-amber-100 blur-3xl opacity-50" />
    </div>
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-24">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="rise rise-1">
            <SectionLabel>24/7 Statewide · Victoria, AU</SectionLabel>
          </div>
          <h1 className="rise rise-2 mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 leading-[1.02]">
            Cheap & Reliable<br />
            <span className="text-[var(--color-primary)]">24/7 Towing</span>{" "}
            Across Victoria
          </h1>
          <p className="rise rise-3 mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
            Starting from just{" "}
            <span className="font-num font-extrabold text-[var(--color-secondary)]">
              $70
            </span>{" "}
            — fast response when you need it most. Cars, caravans, boats, trucks and everything in between.
          </p>

          <div className="rise rise-4 mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`tel:${PRIMARY_PHONE.tel}`}
              data-testid="hero-call-now-button"
              className="inline-flex items-center gap-3 rounded-full bg-[var(--color-secondary)] px-6 py-4 font-display font-extrabold text-white shadow-md hover:-translate-y-0.5 hover:shadow-xl hover:bg-[var(--color-secondary-soft)] transition-all"
            >
              <Phone className="h-5 w-5" />
              <span>Call Now · </span>
              <span className="font-num">{PRIMARY_PHONE.number}</span>
            </a>
            <Link
              to="/contact"
              data-testid="hero-quote-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-6 py-4 font-display font-bold text-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <dl className="rise rise-5 mt-10 grid grid-cols-3 gap-4 max-w-lg">
            {[
              { icon: Star, label: "Rating", value: `${SITE.rating}★`, sub: `${SITE.reviewsCount} reviews` },
              { icon: Clock, label: "Available", value: "24/7", sub: "All days" },
              { icon: Wallet, label: "From", value: "$70", sub: "Affordable" },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-slate-200 bg-white/70 backdrop-blur-sm p-4"
                data-testid={`hero-trust-${s.label.toLowerCase()}`}
              >
                <s.icon className="h-4 w-4 text-[var(--color-primary)]" />
                <dt className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">
                  {s.label}
                </dt>
                <dd className="font-num font-extrabold text-2xl text-slate-900 leading-tight">
                  {s.value}
                </dd>
                <p className="text-xs text-slate-500">{s.sub}</p>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl rise rise-3">
            <img
              src={HERO_IMG}
              alt="Professional tow truck hauling a vehicle"
              className="w-full h-[420px] sm:h-[520px] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
            <div className="absolute left-4 right-4 bottom-4 rounded-xl bg-white/95 backdrop-blur p-4 shadow-md">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--color-primary)] text-white">
                  <Truck className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
                    Average response
                  </p>
                  <p className="font-display font-extrabold text-slate-900">
                    Under 30 minutes in metro Victoria
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -top-4 -left-4 rotate-[-6deg] hidden sm:block rounded-xl bg-amber-300 px-4 py-3 font-display font-extrabold shadow-lg">
            <span className="font-num text-2xl">$70</span>
            <span className="text-xs ml-2 uppercase tracking-wider">Starts From</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const items = [
    "We Tow Everything",
    "Cars · Utes · Vans",
    "Caravans · Boats · RVs",
    "Light & Heavy Trucks",
    "Box Trucks · Forklifts",
    "Accident Recovery",
    "24/7 Roadside",
  ];
  const doubled = [...items, ...items];
  return (
    <section className="bg-slate-900 text-white border-y border-slate-800">
      <div className="overflow-hidden py-5">
        <div className="marquee-track flex gap-12 whitespace-nowrap font-display font-extrabold text-2xl tracking-tight">
          {doubled.map((t, i) => (
            <span key={i} className="flex items-center gap-12">
              <span>{t}</span>
              <span className="text-amber-400">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesGrid = () => {
  const featured = SERVICES.slice(0, 8);
  return (
    <section data-testid="home-services" className="py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <SectionLabel>Our Services</SectionLabel>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 max-w-2xl">
              Every vehicle. Every situation. One affordable team.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-2 text-[var(--color-primary)] font-bold hover:text-[var(--color-primary-soft)]"
          >
            View all services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((s, idx) => {
            const Icon = s.icon;
            const big = idx === 0 || idx === 5;
            return (
              <Link
                to={`/services#${s.slug}`}
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className={`group relative rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg ${
                  big ? "sm:col-span-2 sm:row-span-1 bg-slate-900 text-white border-slate-800" : ""
                }`}
              >
                <div
                  className={`grid h-11 w-11 place-items-center rounded-lg ${
                    big ? "bg-white/10" : "bg-blue-50 text-[var(--color-primary)]"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${big ? "text-amber-300" : ""}`} />
                </div>
                <h3 className={`mt-5 font-display text-xl font-extrabold ${big ? "text-white" : "text-slate-900"}`}>
                  {s.title}
                </h3>
                <p className={`mt-2 text-sm ${big ? "text-slate-300" : "text-slate-600"}`}>
                  {s.short}
                </p>
                <div
                  className={`mt-5 inline-flex items-center gap-1 text-sm font-bold ${
                    big ? "text-amber-300" : "text-[var(--color-primary)]"
                  }`}
                >
                  Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const WhyUs = () => {
  const items = [
    { icon: Wallet, title: "Cheap & Transparent", desc: "Quotes start from $70. No hidden fees. We give you a clear price upfront — every time." },
    { icon: Clock, title: "Fast 24/7 Response", desc: "Operating around the clock with drivers ready to dispatch the moment you call." },
    { icon: Truck, title: "Every Vehicle Type", desc: "Cars, utes, caravans, boats, box trucks, forklifts and heavy vehicles." },
    { icon: ShieldCheck, title: "Professional & Insured", desc: "Trained operators, modern equipment and full insurance for total peace of mind." },
  ];
  return (
    <section data-testid="home-why-us" className="py-24 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <SectionLabel color="amber">Why Choose Us</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
            Locally owned, fairly priced — and on the road every single day.
          </h2>
          <p className="mt-5 text-slate-600 max-w-md leading-relaxed">
            We've built our reputation in Victoria on three things: showing up
            quickly, charging fairly, and treating every vehicle like it's our own.
          </p>
          <a
            href={`tel:${PRIMARY_PHONE.tel}`}
            data-testid="whyus-call-button"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-[var(--color-primary)] px-6 py-3.5 font-display font-extrabold text-white hover:bg-[var(--color-primary-soft)] hover:-translate-y-0.5 transition-all"
          >
            <Phone className="h-4 w-4" />
            Talk to a real person
          </a>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {items.map((it) => (
            <div
              key={it.title}
              className="rounded-xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md"
            >
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-[var(--color-primary)]">
                <it.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-xl font-extrabold text-slate-900">
                {it.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section data-testid="home-testimonials" className="py-24 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <SectionLabel>What customers say</SectionLabel>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 max-w-2xl">
            <span className="font-num text-amber-500">{SITE.rating}★</span> from {SITE.reviewsCount}+ Victorians who needed a tow.
          </h2>
        </div>
        <Link to="/testimonials" data-testid="testimonials-view-all" className="inline-flex items-center gap-2 text-[var(--color-primary)] font-bold hover:text-[var(--color-primary-soft)]">
          Read all reviews <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <figure
            key={t.name}
            data-testid={`testimonial-${i}`}
            className="rounded-2xl border border-slate-200 bg-slate-50 p-7 hover:bg-white hover:-translate-y-1 hover:shadow-lg transition-all"
          >
            <div className="flex gap-1 text-amber-500">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} className="h-4 w-4 fill-current" />
              ))}
            </div>
            <blockquote className="mt-5 font-display text-xl font-bold text-slate-900 leading-snug">
              "{t.quote}"
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--color-primary)] text-white font-display font-bold">
                {t.name.charAt(0)}
              </div>
              <div>
                <p className="font-bold text-slate-900 text-sm">{t.name}</p>
                <p className="text-xs text-slate-500">{t.role}</p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const Coverage = () => (
  <section data-testid="home-coverage" className="py-24 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 items-center">
      <div className="lg:col-span-5">
        <SectionLabel>Coverage</SectionLabel>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900">
          Servicing every corner of Victoria.
        </h2>
        <p className="mt-5 text-slate-600 max-w-md leading-relaxed">
          From metro Melbourne to regional towns — we have trucks ready in Sale,
          Albion and surrounds. If you can call us, we can probably reach you.
        </p>
        <div className="mt-8 flex items-start gap-3 text-slate-700">
          <MapPin className="h-5 w-5 text-[var(--color-secondary)] mt-0.5" />
          <div>
            <p className="font-bold">Main base: Sale, VIC</p>
            <p className="text-sm text-slate-500">Additional location: 31 King Edward Ave, Albion VIC 3020</p>
          </div>
        </div>
      </div>
      <div className="lg:col-span-7">
        <div className="flex flex-wrap gap-2">
          {SITE.serviceArea.map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              <CheckCircle2 className="h-4 w-4 text-[var(--color-primary)]" />
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const QuickContact = () => (
  <section className="py-20 bg-white">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-10 sm:p-14 text-center">
        <Award className="h-8 w-8 mx-auto text-[var(--color-secondary)]" />
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-900">
          Need a tow right now?
        </h2>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto">
          Pick up the phone and speak to a real person — quotes in under a minute.
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`tel:${PRIMARY_PHONE.tel}`}
            data-testid="quickcontact-call"
            className="inline-flex items-center gap-3 rounded-full bg-[var(--color-secondary)] px-6 py-4 font-display font-extrabold text-white hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            <Phone className="h-5 w-5" />
            {PRIMARY_PHONE.number}
          </a>
          <Link
            to="/contact"
            data-testid="quickcontact-quote"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-4 text-white font-display font-bold hover:bg-slate-800 transition-all"
          >
            <MessageSquare className="h-4 w-4" />
            Request a Free Quote
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <ServicesGrid />
      <EmergencyBanner />
      <WhyUs />
      <Testimonials />
      <Coverage />
      <QuickContact />
    </div>
  );
};

export default Home;
